import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Check, Clock3, Heart, MapPin } from 'lucide-react';
import ScrollFadeHeading from './ScrollFadeHeading';

// Story Carousel Component
const StoryCarousel: React.FC = () => {
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const storyImages = [
    '/assets/vanilla_pollinisation.jpeg',
    '/assets/vanilla_flowers.jpeg',
    '/assets/vanilla_branded.jpeg'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % storyImages.length);
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative rounded-3xl overflow-hidden shadow-card h-96 lg:h-[500px]">
      {storyImages.map((image, index) => (
        <motion.img
          key={index}
          src={image}
          alt={`Notre histoire ${index + 1}`}
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{
            opacity: currentImageIndex === index ? 1 : 0,
            scale: currentImageIndex === index ? 1 : 1.1
          }}
          transition={{
            opacity: { duration: 1, ease: "easeInOut" },
            scale: { duration: 8, ease: "easeInOut" }
          }}
        />
      ))}

      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-coffee-200/40 via-transparent to-transparent"></div>

      {/* 8 Mois de Patience - Top Right */}
      <div className="absolute top-6 right-6">
        <motion.button
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          onClick={() => document.getElementById('process')?.scrollIntoView({ behavior: 'smooth' })}
          className="bg-white/60 backdrop-blur-xl p-4 rounded-2xl shadow-card border border-white/60 hover:bg-white/75 hover:shadow-glow transition-all duration-300 cursor-pointer group"
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-forest-200 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
              <Clock3 className="h-5 w-5 text-cream-50" aria-hidden="true" />
            </div>
            <div>
              <div className="font-semibold text-coffee-200 text-sm group-hover:text-forest-200 transition-colors duration-300">{t('story.carousel.patienceTitle', '8 Mois de Patience')}</div>
              <div className="text-xs text-coffee-100">{t('story.carousel.patienceSubtitle', 'Pollinisation à récolte')}</div>
            </div>
          </div>
        </motion.button>
      </div>

      {/* Overlay Content - Bottom Left */}
      <div className="absolute bottom-6 left-6">
        <div className="flex items-center bg-white/60 backdrop-blur-xl px-4 py-3 rounded-2xl border border-white/50 text-coffee-200">
          <MapPin className="mr-2 h-5 w-5 text-forest-200 flex-shrink-0" aria-hidden="true" />
          <span className="font-medium text-sm whitespace-nowrap">{t('story.carousel.region', 'Région SAVA, Madagascar')}</span>
        </div>
      </div>

      {/* Image indicators */}
      <div className="absolute bottom-6 right-6 flex space-x-2">
        {storyImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImageIndex(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              currentImageIndex === index
                ? 'bg-cream-50 shadow-lg scale-110'
                : 'bg-cream-50/50 hover:bg-cream-50/70'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const StorySection: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section id="story" className="py-24">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="order-2 lg:order-1"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="inline-flex items-center px-4 py-2 bg-forest-200/10 border border-forest-200/20 rounded-full text-forest-200 text-sm font-medium mb-6"
            >
              <Heart className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('story.subtitle')}
            </motion.div>

            <ScrollFadeHeading>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-coffee-200 mb-8 leading-tight">
                {t('story.title')}
              </h2>
            </ScrollFadeHeading>

            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-coffee-100 leading-relaxed mb-8">
                {t('story.description')}
              </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
                className="text-center p-6 bg-white/55 backdrop-blur-xl rounded-2xl shadow-card border border-white/60 hover:border-forest-200/40 hover:shadow-glow transition-all duration-300"
              >
                <div className="text-4xl font-bold text-forest-200 mb-2">20+</div>
                <div className="text-sm text-coffee-100 font-medium">{t('story.stats.yearsLabel', "Années d'Excellence")}</div>
                <div className="text-xs text-coffee-100/70 mt-1">{t('story.stats.yearsSub', 'Depuis 2004')}</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
                className="text-center p-6 bg-white/55 backdrop-blur-xl rounded-2xl shadow-card border border-white/60 hover:border-forest-200/40 hover:shadow-glow transition-all duration-300"
              >
                <div className="text-4xl font-bold text-forest-200 mb-2">100+</div>
                <div className="text-sm text-coffee-100 font-medium">{t('story.stats.partnersLabel', 'Fermiers Partenaires')}</div>
                <div className="text-xs text-coffee-100/70 mt-1">{t('story.stats.partnersSub', 'Famille élargie')}</div>
              </motion.div>
            </div>

            {/* Values */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-wrap gap-3"
            >
              {[
                t('story.values.sustainable', 'Agriculture Durable'),
                t('story.values.fairTrade', 'Commerce Équitable'),
                t('story.values.premium', 'Qualité Premium'),
              ].map((value) => (
                <span
                  key={value}
                  className="px-4 py-2 bg-forest-200/10 text-forest-200 rounded-full text-sm font-medium border border-forest-200/20"
                >
                  {value}
                </span>
              ))}
            </motion.div>
          </motion.div>

          {/* Visual Content with Carousel */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="order-1 lg:order-2"
          >
            {/* Main Image Carousel */}
            <StoryCarousel />

            {/* Compact Badge below the image */}
            <div className="mt-6 flex">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="bg-white/55 backdrop-blur-xl px-4 py-3 rounded-2xl shadow-card border border-white/60"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-forest-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="h-5 w-5 text-cream-50" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="font-semibold text-coffee-200 text-sm">{t('story.badge.title', 'Certification Bio')}</div>
                    <div className="text-xs text-coffee-100">{t('story.badge.subtitle', 'Standards internationaux')}</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Section - Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-20 max-w-5xl mx-auto"
        >
          <div className="relative bg-white/60 backdrop-blur-xl rounded-3xl shadow-card border border-white/60 overflow-hidden">
            <div className="grid sm:grid-cols-[auto_1fr] gap-6 sm:gap-10 p-8 lg:p-12 items-center relative z-10">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-forest-200 text-cream-50 flex items-center justify-center shadow-glow flex-shrink-0 mx-auto sm:mx-0">
                <Heart className="h-8 w-8 sm:h-9 sm:w-9" aria-hidden="true" />
              </div>
              <div className="text-center sm:text-left">
                <h3 className="font-display text-2xl lg:text-3xl font-bold text-coffee-200 mb-4">
                  {t('story.mission.title', 'Notre Mission')}
                </h3>
                <p className="text-lg text-coffee-100 leading-relaxed">
                  {t('story.mission.description', "Préserver et transmettre l'art ancestral de la culture de la vanille malgache, tout en accompagnant nos fermiers partenaires vers l'excellence et la prospérité. Chaque gousse que nous exportons porte en elle l'âme de Madagascar et l'engagement de toute une communauté.")}
                </p>
              </div>
            </div>

            {/* Decorative quote mark */}
            <div
              aria-hidden="true"
              className="absolute -right-4 -bottom-16 text-[13rem] font-display font-bold text-forest-200/[0.05] leading-none pointer-events-none select-none"
            >
              "
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default StorySection;
